import { legacyFieldFor, PROVIDER_FIELD } from './legacy-fields';
import { CanonicalProvider, Modality, normalizeProvider } from './providers';

export interface ResolvedModelSelection {
  aiSystem: CanonicalProvider;
  model: string;
  modality: Modality;
}

interface ModelSelection {
  aiSystem?: string;
  model?: string;
}

const AUDIO_SELECTOR: Record<string, { provider: CanonicalProvider; model: string }> = {
  openai: { provider: 'openAi', model: 'whisper-1' },
  gemini: { provider: 'gemini', model: 'gemini-speech-to-text' },
  groq: { provider: 'groq', model: 'whisper-large-v3' },
  groq_v3_turbo: { provider: 'groq', model: 'whisper-large-v3-turbo' },
};

const VOICE_MODEL_BY_PROVIDER: Partial<Record<CanonicalProvider, string>> = {
  evenLabs: 'even-labs-text-to-speech',
  playHt: 'play-ht-text-to-voice',
  groq: 'canopylabs/orpheus-v1-english',
};

function fromCanonical(
  selection: ModelSelection | undefined,
  modality: Modality,
): ResolvedModelSelection | null {
  if (!selection?.aiSystem || !selection?.model) return null;
  const provider = normalizeProvider(selection.aiSystem);
  if (!provider) return null;
  return { aiSystem: provider, model: String(selection.model), modality };
}

export function resolveNodeModel(
  nodeData: Record<string, unknown> | null | undefined,
  modality: Modality,
): ResolvedModelSelection | null {
  if (!nodeData) return null;
  const canonical = fromCanonical(
    nodeData.modelSelection as ModelSelection | undefined,
    modality,
  );
  if (canonical) return canonical;

  if (modality === 'audio') {
    const selector = String(nodeData.aiModel ?? '').trim().toLowerCase();
    const entry = AUDIO_SELECTOR[selector];
    return entry
      ? { aiSystem: entry.provider, model: entry.model, modality }
      : null;
  }

  if (modality === 'voice') {
    const provider = normalizeProvider(nodeData.voiceSystem as string | undefined);
    const model = provider ? VOICE_MODEL_BY_PROVIDER[provider] : undefined;
    return provider && model ? { aiSystem: provider, model, modality } : null;
  }

  const providerRaw = nodeData[PROVIDER_FIELD[modality]] as string | undefined;
  const provider = normalizeProvider(providerRaw);
  if (!provider) return null;
  const field = legacyFieldFor(provider, modality);
  const model = field ? (nodeData[field] as string | undefined) : undefined;
  if (!model) return null;
  return { aiSystem: provider, model: String(model), modality };
}
